import { useState, useEffect } from 'react'
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { experimentalStyled as styled } from '@material-ui/core/styles'
import Scrollbar from 'src/components/Scrollbar'
import ChatHomeListSignWise from './ChatHomeListSignWise'
import ChatSearchSignWise from './ChatSearchSignWise'
import ChatAccountSignWise from './ChatAccountSignWise'
import { MIconButton } from 'src/components/@material-extend'
import { Icon } from '@iconify/react'
import arrowIosBackFill from '@iconify/icons-eva/arrow-ios-back-fill'
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill'
import { Navigate, useNavigate } from 'react-router'
import { PATH_DASHBOARD } from 'src/routes/paths'
import ChatHistory from './ChatHistory'
import { Button, useTheme } from '@mui/material'
import moment from 'moment'
import { NoteAlt } from '@material-ui/icons'
// Sidebar Container
const RootStyle = styled('div')(({ theme, openSidebar }) => ({
  width: openSidebar ? 320 : 120, // Increased from 80 to 120 to show more text
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.mode === 'light' 
    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(147, 197, 253, 0.12) 50%, rgba(219, 234, 254, 0.15) 100%)' // Rich blue gradient
    : 'linear-gradient(135deg, rgba(30, 58, 138, 0.9) 0%, rgba(59, 130, 246, 0.8) 100%)', // Dark blue gradient
  boxShadow: theme.palette.mode === 'light' 
    ? '0 0 20px rgba(0, 0, 0, 0.08)' 
    : '0 0 20px rgba(0, 0, 0, 0.3)',
  height: '100vh',
  position: 'relative',
  transform: openSidebar ? 'translateX(0)' : 'translateX(-10px)',
  opacity: openSidebar ? 1 : 0.95,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'light'
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(147, 197, 253, 0.18) 50%, rgba(219, 234, 254, 0.22) 100%)'
      : 'linear-gradient(135deg, rgba(30, 58, 138, 0.15) 0%, rgba(59, 130, 246, 0.12) 100%)',
    pointerEvents: 'none',
    transition: 'opacity 0.3s ease',
    opacity: openSidebar ? 1 : 0.5,
  },
}))

// Styled components for animations
const AnimatedStack = styled(Stack)(({ theme }) => ({
  opacity: 0,
  transform: 'translateY(-20px)',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  '&.animate': {
    opacity: 1,
    transform: 'translateY(0)',
  },
}))

const AnimatedButton = styled(Button)(({ theme }) => ({
  background: theme.palette.mode === 'light' 
    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 197, 253, 0.25) 100%)' 
    : 'linear-gradient(135deg, rgba(30, 58, 138, 0.3) 0%, rgba(59, 130, 246, 0.2) 100%)',
  color: theme.palette.mode === 'light' 
    ? 'rgba(30, 58, 138, 0.9)' 
    : 'rgba(219, 234, 254, 0.9)',
  border: `1px solid ${theme.palette.mode === 'light' 
    ? 'rgba(59, 130, 246, 0.3)' 
    : 'rgba(147, 197, 253, 0.3)'}`,
  borderRadius: 12,
  px: 2,
  py: 1,
  textTransform: 'none',
  fontWeight: 600,
  opacity: 0,
  transform: 'scale(0.8)',
  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: theme.palette.mode === 'light'
    ? '0 2px 8px rgba(59, 130, 246, 0.15)'
    : '0 2px 8px rgba(30, 58, 138, 0.3)',
  '&.animate': {
    opacity: 1,
    transform: 'scale(1)',
  },
  '&:hover': {
    background: theme.palette.mode === 'light' 
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(147, 197, 253, 0.35) 100%)' 
      : 'linear-gradient(135deg, rgba(30, 58, 138, 0.4) 0%, rgba(59, 130, 246, 0.3) 100%)',
    transform: 'translateY(-2px) scale(1.02)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 8px 20px rgba(59, 130, 246, 0.25)'
      : '0 8px 20px rgba(30, 58, 138, 0.4)',
  },
  '&:active': {
    transform: 'translateY(0) scale(0.98)',
  },
}))

const AnimatedScrollbar = styled(Scrollbar)(({ theme }) => ({
  flexGrow: 1,
  opacity: 0,
  transform: 'translateY(30px)',
  transition: 'all 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
  background: theme.palette.mode === 'light' 
    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 197, 253, 0.08) 100%)' 
    : 'linear-gradient(135deg, rgba(30, 58, 138, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
  borderRadius: 12,
  // margin: '8px',
  // padding: '4px',
  '&.animate': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '& .simplebar-scrollbar': {
    '&:before': {
      background: theme.palette.mode === 'light' 
        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.6) 0%, rgba(147, 197, 253, 0.8) 100%)' 
        : 'linear-gradient(135deg, rgba(30, 58, 138, 0.8) 0%, rgba(59, 130, 246, 0.6) 100%)',
      transition: 'all 0.3s ease',
      borderRadius: 6,
    },
  },
  '& .simplebar-track': {
    background: theme.palette.mode === 'light' 
      ? 'rgba(59, 130, 246, 0.08)' 
      : 'rgba(30, 58, 138, 0.15)',
    borderRadius: 6,
  },
}))

export default function ChatSidebarSignWise({
  teamMembers,
  setSelectedContact,
  selectedContact,
  setCompose,
  openCompose,
  setOpen,
  refreshChat,
  setRenderChat,
  renderChat,
  status,
  setStatus,
  refresh,
  setRefresh,
  showHistory,
  setShowHistory,
  chatOpenTime,
  setChatOpenTime,
  historyMessage,
  setHistoryMessage,
  selectedChatLogId,
  setSelectedChatLogId,
  newChat,
  setNewChat,
  handleRefresh,
  setMobileChat,
}) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [openSidebar, setOpenSidebar] = useState(true)
  const [isAnimated, setIsAnimated] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))

  const filteredMembers = teamMembers.filter(
    (member) =>
      member.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.group_name?.toLowerCase().includes(searchQuery.toLowerCase()),
    // member.email?.toLowerCase().includes(searchQuery.toLowerCase()),
  )
  useEffect(() => {
    if (!openSidebar) {
      setSearchQuery('') // Reset search when sidebar is collapsed
    }
  }, [openSidebar])

  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => {
      setIsAnimated(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  return (
    <RootStyle openSidebar={openSidebar}>
      <Box
        sx={{
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: openSidebar ? 'space-between' : 'center',
          position: 'relative',
          zIndex: 1,
          background: theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 197, 253, 0.20) 50%, rgba(219, 234, 254, 0.25) 100%)'
            : 'linear-gradient(135deg, rgba(30, 58, 138, 0.2) 0%, rgba(59, 130, 246, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          // borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {openSidebar && (
          <ChatAccountSignWise
            status={status}
            setStatus={setStatus}
            refresh={refresh}
            setRefresh={setRefresh}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={0.4}>
          <Tooltip title={openSidebar ? "Collapse Sidebar" : "Expand Sidebar"}>
            <MIconButton 
              onClick={() => setOpenSidebar(!openSidebar)}
              sx={{
                background: theme.palette.mode === 'light' 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 197, 253, 0.25) 100%)' 
                  : 'linear-gradient(135deg, rgba(30, 58, 138, 0.3) 0%, rgba(59, 130, 246, 0.2) 100%)',
                color: theme.palette.mode === 'light' 
                  ? 'rgba(30, 58, 138, 0.9)' 
                  : 'rgba(219, 234, 254, 0.9)',
                border: `1px solid ${theme.palette.mode === 'light' 
                  ? 'rgba(59, 130, 246, 0.3)' 
                  : 'rgba(147, 197, 253, 0.3)'}`,
                boxShadow: theme.palette.mode === 'light'
                  ? '0 2px 6px rgba(59, 130, 246, 0.15)'
                  : '0 2px 6px rgba(30, 58, 138, 0.3)',
                '&:hover': {
                  background: theme.palette.mode === 'light' 
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(147, 197, 253, 0.35) 100%)' 
                    : 'linear-gradient(135deg, rgba(30, 58, 138, 0.4) 0%, rgba(59, 130, 246, 0.3) 100%)',
                  transform: 'scale(1.1) rotate(5deg)',
                  boxShadow: theme.palette.mode === 'light'
                    ? '0 6px 16px rgba(59, 130, 246, 0.25)'
                    : '0 6px 16px rgba(30, 58, 138, 0.4)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <Icon
                width={20}
                height={20}
                icon={openSidebar ? arrowIosBackFill : arrowIosForwardFill}
                style={{
                  transition: 'transform 0.3s ease',
                  transform: openSidebar ? 'rotate(0deg)' : 'rotate(180deg)',
                }}
              />
            </MIconButton>
          </Tooltip>
          {openSidebar && (
            <Tooltip title="Add New Group">
              <MIconButton
                sx={{
                  backgroundColor: theme.palette.mode === 'light' 
                    ? 'rgba(2, 125, 194, 0.1)' 
                    : 'rgba(2, 125, 194, 0.2)',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' 
                      ? 'rgba(2, 125, 194, 0.15)' 
                      : 'rgba(2, 125, 194, 0.3)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
                onClick={() => {
                  if (selectedContact) {
                    setCompose(!openCompose)
                    setShowHistory(false)
                    setSelectedContact(null)
                  }
                }}
              >
                <Stack
                  className="material-symbols--group-add-outline"
                  sx={{
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: 'inherit',
                  }}
                />
              </MIconButton>
            </Tooltip>
          )}
          {/* {openSidebar && (
            <MIconButton>
              <Stack
                className="ic--round-open-in-full"
                sx={{
                  fontSize: '1.3rem',
                  cursor: 'pointer',
                  color: 'text.secondary',  
                }}
                onClick={() => {
                  navigate(PATH_DASHBOARD.chatSignwise.root , {state :{showHistory:showHistory}});
                  setOpen(false);
                }}
              />
            </MIconButton>
          )} */}
        </Stack>
      </Box>

      {/* Search Bar / New Chat Button */}
      {openSidebar ? (
        <AnimatedStack className={isAnimated ? 'animate' : ''} sx={{ py: 1, px: 3 }}>
          {!selectedContact && !showHistory ? (
            <ChatSearchSignWise query={searchQuery} setQuery={setSearchQuery} />
          ) : (
            <>
              <AnimatedButton
                className={isAnimated ? 'animate' : ''}
                onClick={() => {
                  let currentUnixTime = moment().unix()
                  setChatOpenTime(currentUnixTime)
                  setSelectedChatLogId(null)
                  setNewChat(!newChat)
                }}
              >
                <NoteAlt sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  New Chat
                </Typography>
              </AnimatedButton>
            </>
          )}
        </AnimatedStack>
      ) : ( selectedContact ? (
        // Collapsed state - show new chat icon
        <AnimatedStack 
          className={isAnimated ? 'animate' : ''}
          sx={{ 
            py: 1,
            px: 2, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Tooltip title="New Chat">
            <MIconButton
              onClick={() => {
                let currentUnixTime = moment().unix()
                setChatOpenTime(currentUnixTime)
                setSelectedChatLogId(null)
                setNewChat(!newChat)
              }}
              sx={{
                background: theme.palette.mode === 'light' 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 197, 253, 0.25) 100%)' 
                  : 'linear-gradient(135deg, rgba(30, 58, 138, 0.3) 0%, rgba(59, 130, 246, 0.2) 100%)',
                color: theme.palette.mode === 'light' 
                  ? 'rgba(30, 58, 138, 0.9)' 
                  : 'rgba(219, 234, 254, 0.9)',
                border: `1px solid ${theme.palette.mode === 'light' 
                  ? 'rgba(59, 130, 246, 0.3)' 
                  : 'rgba(147, 197, 253, 0.3)'}`,
                width: 40,
                height: 40,
                boxShadow: theme.palette.mode === 'light'
                  ? '0 2px 6px rgba(59, 130, 246, 0.15)'
                  : '0 2px 6px rgba(30, 58, 138, 0.3)',
                '&:hover': {
                  background: theme.palette.mode === 'light' 
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(147, 197, 253, 0.35) 100%)' 
                    : 'linear-gradient(135deg, rgba(30, 58, 138, 0.4) 0%, rgba(59, 130, 246, 0.3) 100%)',
                  transform: 'scale(1.1) rotate(5deg)',
                  boxShadow: theme.palette.mode === 'light'
                    ? '0 6px 16px rgba(59, 130, 246, 0.25)'
                    : '0 6px 16px rgba(30, 58, 138, 0.4)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <NoteAlt sx={{ fontSize: 20 }} />
            </MIconButton>
          </Tooltip>
        </AnimatedStack> 
      ) : (<></>))}

      {/* Contact List */}
      <AnimatedScrollbar 
        className={isAnimated ? 'animate' : ''}
        sx={{ 
          '& .simplebar-scrollbar': {
            '&:before': {
              backgroundColor: openSidebar 
                ? theme.palette.mode === 'light' 
                  ? 'rgba(0, 0, 0, 0.2)' 
                  : 'rgba(255, 255, 255, 0.2)'
                : theme.palette.mode === 'light' 
                  ? 'rgba(2, 125, 194, 0.3)' 
                  : 'rgba(2, 125, 194, 0.5)',
            },
          },
        }}
      >
        {!selectedContact && !showHistory ? (
          <ChatHomeListSignWise
            setMobileChat={setMobileChat}
            openSidebar={openSidebar}
            teamMembers={filteredMembers}
            onSelectContact={setSelectedContact}
            setCompose={setCompose}
            openCompose={openCompose}
            setRenderChat={setRenderChat}
            renderChat={renderChat}
            setShowHistory={setShowHistory}
            showHistory={showHistory}
            chatOpenTime={chatOpenTime}
            setChatOpenTime={setChatOpenTime}
            selectedChatLogId={selectedChatLogId}
            setSelectedChatLogId={setSelectedChatLogId}
            setHistoryMessage={setHistoryMessage}
          />
        ) : (
          <ChatHistory
            // openSidebar={openSidebar}
            // teamMembers={filteredMembers}
            // onSelectContact={setSelectedContact}
            // setCompose={setCompose}
            // openCompose={openCompose}
            // setRenderChat={setRenderChat}
            // renderChat={renderChat}
            // setShowHistory={setShowHistory}
            // showHistory={showHistory}
            setHistoryMessage={setHistoryMessage}
            historyMessage={historyMessage}
            selectedChatLogId={selectedChatLogId}
            setSelectedChatLogId={setSelectedChatLogId}
            handleRefresh={handleRefresh}
          />
        )}
      </AnimatedScrollbar>
    </RootStyle>
  )
}
